// ============================================================================
// Cartes de remuneration (F-755/757/758/759/760/761/762) — Zone A detail
// ============================================================================
// Regroupe les surfaces d'ecriture de la remuneration (parite « feature file » du
// parc gest, ex. FinanceVisuals) : primes, historique salarial, heures, avantages,
// boni, evaluations. Chacune branche une mutation useManagement + reflete son etat.
// Aucune garde d'autorite ici (M-750/M-778) : le hub ne rend ces cartes que
// derriere le grant serveur ; un refus revient en message via onError.

import { useState } from 'react';
import { GS_COMPONENTS } from '@gs/ui';
import { CalendarClock, Plus, Save } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { Card, CardHeader, Badge, DataRow, EmptyRow, Modal, ModalFooter } from './ui';
import {
  isSunday, isSaturday, weekSunday, weekSaturday, periodCost, isPeriodActive,
  overridesSupplement, premiumsSupplement, employerBenefitsAnnual,
} from '../../lib/salary/special-hours';
import { bonusAmount } from '../../lib/salary/payroll';
import { formatDecimal } from '../salary/format';
import {
  managementErrorCode,
  useAddRatePremium, useRemoveRatePremium, useSaveSalaryHistory, useDeleteSalaryHistory,
  useSetWork, useAddHoursOverride, useRemoveHoursOverride, useSetBenefits, useSetBonus,
  useDeleteBonus, useSubmitEvaluation,
  type ManagementCompensationView, type HoursOverride, type RatePremium,
  type SalaryHistoryEntry, type BonusEntry, type Evaluation, type EvalConfig,
} from '../../hooks/useManagement';

const CAD0 = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
const money = (n: number) => CAD0.format(n);
type OnError = (code: string) => void;
const label = 'text-meta font-medium text-slate-600 dark:text-slate-300';

// ─── F-755 Primes ponctuelles ─────────────────────────────────────────────────

export function RatePremiumCard({ email, premiums, baseWeeklyHours, overrides, year, onError }: {
  email: string; premiums: RatePremium[]; baseWeeklyHours: number; overrides: HoursOverride[]; year: number; onError: OnError;
}) {
  const { t } = useTranslation();
  const add = useAddRatePremium();
  const remove = useRemoveRatePremium();
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const total = premiums.length ? premiumsSupplement(premiums.map(toLibPremium), baseWeeklyHours, overrides.map(toLibOverride), year) : 0;

  function submit() {
    const s = start ? weekSunday(start) : '';
    const e = end ? weekSaturday(end) : '';
    if (!s || !e || !isSunday(s) || !isSaturday(e) || e < s) { setErr(t('gest:management.errors.invalid_period')); return; }
    if (!amount || !reason.trim()) { setErr(t('gest:management.errors.invalid')); return; }
    setErr(null);
    add.mutate({ email, payload: { startDate: s, endDate: e, premiumPerHour: Number(amount), reason: reason.trim() } }, {
      onSuccess: () => { setOpen(false); setStart(''); setEnd(''); setAmount(''); setReason(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.premium.title')} action={<button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><Plus size={16} aria-hidden="true" />{t('gest:management.premium.add')}</button>} />
      <p className="mb-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.premium.hint')}</p>
      {premiums.length === 0 ? (
        <EmptyRow title={t('gest:management.premium.empty')} description={t('gest:management.premium.emptyHint')} />
      ) : (
        <ul className="flex flex-col gap-2">
          {premiums.map((p) => {
            const active = p.startDate <= today && today <= p.endDate;
            const past = p.endDate < today;
            const cost = premiumsSupplement([toLibPremium(p)], baseWeeklyHours, overrides.map(toLibOverride), year);
            return (
              <li key={p.id}>
                <DataRow
                  leading={<Badge tone={active ? 'info' : 'neutral'}>{active ? t('gest:management.common.active') : past ? t('gest:management.common.ended') : t('gest:management.common.upcoming')}</Badge>}
                  title={`${p.startDate} -> ${p.endDate} · +${money(p.premiumPerHour)}/h`}
                  subtitle={p.reason}
                  trailing={<>
                    <span className="text-field font-medium tabular-nums text-slate-700 dark:text-slate-200">{money(cost)}</span>
                    <button type="button" onClick={() => remove.mutate({ email, payload: { premiumId: p.id } }, { onError: (x) => onError(managementErrorCode(x)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>{t('gest:management.common.delete')}</button>
                  </>}
                />
              </li>
            );
          })}
        </ul>
      )}
      {premiums.length > 0 ? <p className="mt-2 text-field text-slate-600 dark:text-slate-300">{t('gest:management.premium.annualImpact', { amount: money(total) })}</p> : null}

      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.premium.dialogTitle')}>
        <PeriodFields startId="prem" start={start} end={end} onStart={setStart} onEnd={setEnd} t={t} />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="prem-amount" className={label}>{t('gest:management.premium.amount')}</label>
          <input id="prem-amount" type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={GS_COMPONENTS.input} />
        </div>
        <ReasonField id="prem-reason" value={reason} onChange={setReason} t={t} />
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.premium.submit')} onSubmit={submit} pending={add.isPending} />
      </Modal>
    </Card>
  );
}

// ─── F-757 Historique salarial (dialogue declenche par ClassGrid) ────────────

export function SalaryHistoryDialog({ email, entries, currentYear, onError }: {
  email: string; entries: SalaryHistoryEntry[]; currentYear: number; onError: OnError;
}) {
  const { t } = useTranslation();
  const save = useSaveSalaryHistory();
  const del = useDeleteSalaryHistory();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(currentYear - 1));
  const [role, setRole] = useState('');
  const [annual, setAnnual] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const sorted = [...entries].sort((a, b) => b.year - a.year);

  function submit() {
    const y = Number(year);
    if (!Number.isInteger(y) || !role.trim() || !annual) { setErr(t('gest:management.errors.invalid')); return; }
    if (y >= currentYear) { setErr(t('gest:management.errors.past_only')); return; }
    setErr(null);
    save.mutate({ email, payload: { year: y, role: role.trim(), annual: Number(annual), note: note.trim() || null } }, {
      onSuccess: () => { setRole(''); setAnnual(''); setNote(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={GS_COMPONENTS.btnSecondary}>{t('gest:management.salaryHistory.open')}</button>
      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.salaryHistory.title')} size="lg">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="flex flex-col gap-1.5"><label htmlFor="sh-year" className={label}>{t('gest:management.salaryHistory.year')}</label><input id="sh-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} className={GS_COMPONENTS.input} /></div>
          <div className="flex flex-col gap-1.5"><label htmlFor="sh-role" className={label}>{t('gest:management.salaryHistory.role')}</label><input id="sh-role" value={role} onChange={(e) => setRole(e.target.value)} className={GS_COMPONENTS.input} /></div>
          <div className="flex flex-col gap-1.5"><label htmlFor="sh-annual" className={label}>{t('gest:management.salaryHistory.annual')}</label><input id="sh-annual" type="number" min={0} value={annual} onChange={(e) => setAnnual(e.target.value)} className={GS_COMPONENTS.input} /></div>
          <div className="flex flex-col gap-1.5"><label htmlFor="sh-note" className={label}>{t('gest:management.salaryHistory.note')}</label><input id="sh-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className={GS_COMPONENTS.input} /></div>
        </div>
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <div className="flex justify-end"><button type="button" onClick={submit} disabled={save.isPending} className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}><Save size={16} aria-hidden="true" />{t('gest:management.salaryHistory.save')}</button></div>
        {sorted.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {sorted.map((h) => (
              <li key={h.year}><DataRow title={`${h.year} · ${h.role ?? '—'}`} subtitle={money(h.annual)} trailing={<button type="button" onClick={() => del.mutate({ email, payload: { year: h.year } }, { onError: (x) => onError(managementErrorCode(x)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>{t('gest:management.common.delete')}</button>} /></li>
            ))}
          </ul>
        ) : <EmptyRow title={t('gest:management.salaryHistory.empty')} />}
      </Modal>
    </>
  );
}

// ─── F-758/759 Horaire (heures normales + heures speciales) ──────────────────

export function HoursCard({ email, comp, currentYear, specialRate, onError }: {
  email: string; comp: ManagementCompensationView; currentYear: number; specialRate: number | null; onError: OnError;
}) {
  const { t, i18n } = useTranslation();
  const setWork = useSetWork();
  const addOv = useAddHoursOverride();
  const removeOv = useRemoveHoursOverride();
  const today = new Date().toISOString().slice(0, 10);

  const [editOpen, setEditOpen] = useState(false);
  const [hours, setHours] = useState(comp.work?.weeklyHours != null ? String(comp.work.weeklyHours) : '');
  const [tasks, setTasks] = useState((comp.work?.mainTasks ?? []).join('\n'));

  const [ovOpen, setOvOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [ovHours, setOvHours] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const overrides = [...comp.overrides].sort((a, b) => b.startDate.localeCompare(a.startDate));
  // Heures de base : reelles si renseignees, sinon 40 (defaut parc — la classe
  // n'est pas re-resolue ici, le supplement reste une estimation d'affichage).
  const baseWeekly = comp.work?.weeklyHours ?? 40;
  const supplement = specialRate != null && overrides.length ? overridesSupplement(overrides.map(toLibOverride), baseWeekly, specialRate, comp.overtimeRule, currentYear) : 0;

  function saveWork() {
    setWork.mutate({ email, payload: { weeklyHours: hours === '' ? null : Number(hours), mainTasks: tasks.trim() ? tasks : null } }, {
      onSuccess: () => setEditOpen(false),
      onError: (x) => onError(managementErrorCode(x)),
    });
  }
  function addOverride() {
    const s = start ? weekSunday(start) : '';
    const e = end ? weekSaturday(end) : '';
    if (!s || !e || !isSunday(s) || !isSaturday(e) || e < s) { setErr(t('gest:management.errors.invalid_period')); return; }
    if (ovHours === '' || !reason.trim()) { setErr(t('gest:management.errors.invalid')); return; }
    setErr(null);
    addOv.mutate({ email, payload: { startDate: s, endDate: e, weeklyHours: Number(ovHours), reason: reason.trim() } }, {
      onSuccess: () => { setOvOpen(false); setStart(''); setEnd(''); setOvHours(''); setReason(''); },
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader title={t('gest:management.work.title')} action={<button type="button" onClick={() => setEditOpen(true)} className={GS_COMPONENTS.btnSecondary}>{t('gest:management.common.edit')}</button>} />
        <p className="text-field text-slate-700 dark:text-slate-200">{comp.work?.weeklyHours != null ? t('gest:management.work.hoursPerWeek', { hours: comp.work.weeklyHours }) : t('gest:management.work.hoursUnset')}</p>
        {comp.work?.mainTasks && comp.work.mainTasks.length > 0 ? (
          <ul className="ml-4 mt-1 list-disc text-field text-slate-500 dark:text-slate-400">{comp.work.mainTasks.map((tk, i) => <li key={i}>{tk}</li>)}</ul>
        ) : <p className="mt-1 text-field text-slate-500 dark:text-slate-400">{t('gest:management.work.noTasks')}</p>}
      </Card>

      <Card>
        <CardHeader title={t('gest:management.special.title')} action={<button type="button" onClick={() => setOvOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><CalendarClock size={16} aria-hidden="true" />{t('gest:management.special.add')}</button>} />
        <p className="mb-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.special.hint', { threshold: formatDecimal(comp.overtimeRule.weeklyThresholdHours, i18n.language), multiplier: formatDecimal(comp.overtimeRule.multiplier, i18n.language) })}</p>
        {overrides.length === 0 ? <EmptyRow title={t('gest:management.special.empty')} description={t('gest:management.special.emptyHint')} /> : (
          <ul className="flex flex-col gap-2">
            {overrides.map((o) => {
              const active = isPeriodActive(toLibOverride(o), today);
              const past = o.endDate < today;
              const cost = specialRate != null ? periodCost(toLibOverride(o), specialRate, comp.overtimeRule) : null;
              return (
                <li key={o.id}><DataRow
                  leading={<Badge tone={active ? 'info' : 'neutral'}>{active ? t('gest:management.common.active') : past ? t('gest:management.common.ended') : t('gest:management.common.upcoming')}</Badge>}
                  title={`${o.startDate} -> ${o.endDate} · ${o.weeklyHours} h/${t('gest:management.common.week')}`}
                  subtitle={o.reason}
                  trailing={<>
                    {o.weeklyHours === 0 ? <span className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.special.reduction')}</span> : cost != null ? <span className="text-field font-medium tabular-nums text-slate-700 dark:text-slate-200">{money(cost)}</span> : null}
                    <button type="button" onClick={() => removeOv.mutate({ email, payload: { overrideId: o.id } }, { onError: (x) => onError(managementErrorCode(x)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>{t('gest:management.common.delete')}</button>
                  </>}
                /></li>
              );
            })}
          </ul>
        )}
        {overrides.length > 0 && specialRate != null ? <p className="mt-2 text-field text-slate-600 dark:text-slate-300">{t('gest:management.special.annualImpact', { sign: supplement >= 0 ? '+' : '−', amount: money(Math.abs(supplement)) })}</p> : null}
      </Card>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title={t('gest:management.work.dialogTitle')}>
        <div className="flex flex-col gap-1.5"><label htmlFor="wk-hours" className={label}>{t('gest:management.work.hoursLabel')}</label><input id="wk-hours" type="number" min={0} max={168} step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} className={`${GS_COMPONENTS.input} sm:max-w-48`} /></div>
        <div className="flex flex-col gap-1.5"><label htmlFor="wk-tasks" className={label}>{t('gest:management.work.tasksLabel')}</label><textarea id="wk-tasks" rows={5} value={tasks} onChange={(e) => setTasks(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <ModalFooter onCancel={() => setEditOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.work.save')} onSubmit={saveWork} pending={setWork.isPending} />
      </Modal>

      <Modal open={ovOpen} onClose={() => setOvOpen(false)} title={t('gest:management.special.dialogTitle')}>
        <PeriodFields startId="ov" start={start} end={end} onStart={setStart} onEnd={setEnd} t={t} />
        <div className="flex flex-col gap-1.5"><label htmlFor="ov-hours" className={label}>{t('gest:management.special.hoursLabel')}</label><input id="ov-hours" type="number" min={0} max={168} step="0.5" value={ovHours} onChange={(e) => setOvHours(e.target.value)} className={`${GS_COMPONENTS.input} sm:max-w-48`} /></div>
        <ReasonField id="ov-reason" value={reason} onChange={setReason} t={t} />
        {err ? <p role="alert" className="text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <ModalFooter onCancel={() => setOvOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.special.submit')} onSubmit={addOverride} pending={addOv.isPending} />
      </Modal>
    </div>
  );
}

// ─── F-760 Avantages ─────────────────────────────────────────────────────────

export function BenefitsCard({ email, comp, currentYear, onError }: {
  email: string; comp: ManagementCompensationView; currentYear: number; onError: OnError;
}) {
  const { t } = useTranslation();
  const save = useSetBenefits();
  const r = comp.record;
  const [insurance, setInsurance] = useState(r?.insuranceBiweekly != null ? String(r.insuranceBiweekly) : '');
  const [cell, setCell] = useState(r?.cellAllowanceBiweekly != null ? String(r.cellAllowanceBiweekly) : '');
  const [other, setOther] = useState(r?.otherAllowanceBiweekly != null ? String(r.otherAllowanceBiweekly) : '');
  const [vehicle, setVehicle] = useState(r?.hasCompanyVehicle ?? false);
  const num = (s: string) => (s === '' ? null : Number(s));
  const total = r ? employerBenefitsAnnual({ insurance_biweekly: r.insuranceBiweekly, cell_allowance_biweekly: r.cellAllowanceBiweekly, other_allowance_biweekly: r.otherAllowanceBiweekly, has_company_vehicle: r.hasCompanyVehicle, vehicle_cost_biweekly: comp.vehicleCostBiweekly }) : 0;

  function submit() {
    save.mutate({ email, payload: { insuranceBiweekly: num(insurance), cellAllowanceBiweekly: num(cell), otherAllowanceBiweekly: num(other), hasCompanyVehicle: vehicle } }, { onError: (x) => onError(managementErrorCode(x)) });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.benefits.title')} action={<Badge tone={total > 0 ? 'info' : 'neutral'}>{total > 0 ? t('gest:management.benefits.perYear', { amount: money(total) }) : t('gest:management.benefits.none')}</Badge>} />
      <form className="grid grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="flex flex-col gap-1.5"><label htmlFor="ben-ins" className={label}>{t('gest:management.benefits.insurance')}</label><input id="ben-ins" type="number" min={0} step="0.01" value={insurance} onChange={(e) => setInsurance(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <div className="flex flex-col gap-1.5"><label htmlFor="ben-cell" className={label}>{t('gest:management.benefits.cell')}</label><input id="ben-cell" type="number" min={0} step="0.01" value={cell} onChange={(e) => setCell(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <div className="flex flex-col gap-1.5"><label htmlFor="ben-other" className={label}>{t('gest:management.benefits.other')}</label><input id="ben-other" type="number" min={0} step="0.01" value={other} onChange={(e) => setOther(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <label htmlFor="ben-vehicle" className="flex flex-col justify-end gap-1.5">
          <span className="flex items-center gap-2 text-field text-slate-700 dark:text-slate-200"><input id="ben-vehicle" type="checkbox" checked={vehicle} onChange={(e) => setVehicle(e.target.checked)} className="size-4 rounded-sm border-slate-300 text-brand-600 dark:border-slate-600" />{t('gest:management.benefits.vehicle')}</span>
          <span className="text-chip text-slate-500 dark:text-slate-400">{comp.vehicleCostBiweekly != null ? t('gest:management.benefits.vehicleCost', { biweekly: money(comp.vehicleCostBiweekly), annual: money(comp.vehicleCostBiweekly * 26), year: currentYear }) : t('gest:management.benefits.vehicleUnset', { year: currentYear })}</span>
        </label>
        <div className="col-span-2 flex items-center justify-between gap-3">
          <p className="text-chip text-slate-500 dark:text-slate-400">{t('gest:management.benefits.footer')}</p>
          <button type="submit" disabled={save.isPending} className={GS_COMPONENTS.btnAction}>{t('gest:management.common.save')}</button>
        </div>
      </form>
    </Card>
  );
}

// ─── F-761 Boni ───────────────────────────────────────────────────────────────

export function BonusCard({ email, bonusList, bonusBase, currentYear, onError }: {
  email: string; bonusList: BonusEntry[]; bonusBase: number; currentYear: number; onError: OnError;
}) {
  const { t } = useTranslation();
  const save = useSetBonus();
  const del = useDeleteBonus();
  const [year, setYear] = useState(currentYear);
  const byYear = new Map(bonusList.map((b) => [b.year, b]));
  const editing = byYear.get(year) ?? null;
  const [target, setTarget] = useState(editing ? String(editing.targetPct * 100) : '');
  const [actual, setActual] = useState(editing ? String(editing.actualPct * 100) : '');
  const [note, setNote] = useState(editing?.note ?? '');
  const [err, setErr] = useState<string | null>(null);
  const years = [currentYear + 2, currentYear + 1, currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4, currentYear - 5];
  const sorted = [...bonusList].sort((a, b) => b.year - a.year);

  function pickYear(y: number) {
    setYear(y);
    const e = byYear.get(y) ?? null;
    setTarget(e ? String(e.targetPct * 100) : '');
    setActual(e ? String(e.actualPct * 100) : '');
    setNote(e?.note ?? '');
  }
  function submit() {
    if (target === '' || actual === '') { setErr(t('gest:management.errors.invalid')); return; }
    setErr(null);
    save.mutate({ email, payload: { year, targetPct: Number(target), actualPct: Number(actual), note: note.trim() || null } }, { onError: (x) => onError(managementErrorCode(x)) });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.bonus.title')} />
      <form className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-3 dark:border-slate-700 dark:bg-slate-900/40" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="flex flex-col gap-1.5"><label htmlFor="bn-year" className={label}>{t('gest:management.bonus.year')}</label>
          <select id="bn-year" value={year} onChange={(e) => pickYear(Number(e.target.value))} className={GS_COMPONENTS.input}>
            {years.map((y) => <option key={y} value={y}>{y > currentYear ? t('gest:management.bonus.yearProjected', { year: y }) : y === currentYear ? String(y) : t('gest:management.bonus.yearPaid', { year: y })}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5"><label htmlFor="bn-target" className={label}>{t('gest:management.bonus.targetPct')}</label><input id="bn-target" type="number" min={0} max={100} step="0.1" value={target} onChange={(e) => setTarget(e.target.value)} className={GS_COMPONENTS.input} placeholder="15" /></div>
        <div className="flex flex-col gap-1.5"><label htmlFor="bn-actual" className={label}>{t('gest:management.bonus.actualPct')}</label><input id="bn-actual" type="number" min={0} max={100} step="0.1" value={actual} onChange={(e) => setActual(e.target.value)} className={GS_COMPONENTS.input} placeholder="10" /></div>
        <div className="col-span-full flex flex-col gap-1.5"><label htmlFor="bn-note" className={label}>{t('gest:management.bonus.note')}</label><input id="bn-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className={GS_COMPONENTS.input} /></div>
        {err ? <p role="alert" className="col-span-full text-field text-red-600 dark:text-red-400">{err}</p> : null}
        <div className="col-span-full flex items-center justify-between gap-3">
          <p className="text-chip text-slate-500 dark:text-slate-400">{t('gest:management.bonus.hint')}</p>
          <button type="submit" disabled={save.isPending} className={GS_COMPONENTS.btnAction}>{editing ? t('gest:management.common.update') : t('gest:management.common.save')}</button>
        </div>
      </form>
      {sorted.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {sorted.map((b) => (
            <li key={b.year}><DataRow
              title={`${b.year}${b.year > currentYear ? ` · ${t('gest:management.bonus.projectedTag')}` : ''}`}
              subtitle={t('gest:management.bonus.rowScores', { target: (b.targetPct * 100).toFixed(1), actual: (b.actualPct * 100).toFixed(1) })}
              trailing={<>
                <span className="text-field font-medium tabular-nums text-slate-700 dark:text-slate-200">{b.year === currentYear ? money(bonusAmount(bonusBase, b.actualPct)) : '—'}</span>
                <button type="button" onClick={() => pickYear(b.year)} className={GS_COMPONENTS.btnGhost}>{t('gest:management.common.edit')}</button>
                <button type="button" onClick={() => del.mutate({ email, payload: { year: b.year } }, { onError: (x) => onError(managementErrorCode(x)) })} className={`${GS_COMPONENTS.btnGhost} text-red-600 dark:text-red-400`}>{t('gest:management.common.delete')}</button>
              </>}
            /></li>
          ))}
        </ul>
      ) : <p className="mt-3 text-field text-slate-500 dark:text-slate-400">{t('gest:management.bonus.empty')}</p>}
    </Card>
  );
}

// ─── F-762 Evaluations ────────────────────────────────────────────────────────

export function EvaluationsCard({ email, evaluations, evalConfig, canWrite, currentYear, onError }: {
  email: string; evaluations: Evaluation[]; evalConfig: EvalConfig | null; canWrite: boolean; currentYear: number; onError: OnError;
}) {
  const { t } = useTranslation();
  const submit = useSubmitEvaluation();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(currentYear));
  const [comments, setComments] = useState('');
  const [scores, setScores] = useState<Record<string, string>>({});
  const [singleScore, setSingleScore] = useState('0');
  const criteria = evalConfig?.criteria ?? [];

  function send(status: 'draft' | 'submitted') {
    const criteriaScores: Record<string, number> = {};
    for (const c of criteria) criteriaScores[c.key] = Number(scores[c.key] ?? 0);
    submit.mutate({ email, payload: { year: Number(year), comments: comments.trim() || null, status, criteriaScores, score: criteria.length === 0 ? Number(singleScore) : undefined } }, {
      onSuccess: () => setOpen(false),
      onError: (x) => onError(managementErrorCode(x)),
    });
  }

  return (
    <Card>
      <CardHeader title={t('gest:management.eval.title')} action={canWrite ? <button type="button" onClick={() => setOpen(true)} className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}><Plus size={16} aria-hidden="true" />{t('gest:management.eval.new')}</button> : undefined} />
      {evaluations.length === 0 ? <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:management.eval.none')}</p> : (
        <ul className="flex flex-col">
          {evaluations.map((ev) => (
            <li key={ev.id} className="flex items-baseline justify-between gap-3 border-b border-slate-200 py-2 text-field last:border-b-0 dark:border-slate-700">
              <span className="flex items-center gap-2 font-medium tabular-nums text-slate-700 dark:text-slate-200">{ev.year}{ev.status === 'draft' ? <Badge tone="neutral">{t('gest:management.eval.draft')}</Badge> : null}</span>
              <span className="text-slate-500 dark:text-slate-400">{ev.score}{evalConfig ? `/${evalConfig.maxScore}` : ''} · {ev.status === 'draft' ? t('gest:management.eval.notSubmitted') : ev.passed ? (ev.applied ? t('gest:management.eval.applied') : t('gest:management.eval.proposed')) : t('gest:management.eval.below')}</span>
            </li>
          ))}
        </ul>
      )}
      {evalConfig ? <p className="mt-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.eval.threshold', { threshold: evalConfig.passThreshold, max: evalConfig.maxScore })}</p> : null}

      <Modal open={open} onClose={() => setOpen(false)} title={t('gest:management.eval.dialogTitle')} size="lg">
        <div className="flex flex-col gap-1.5"><label htmlFor="ev-year" className={label}>{t('gest:management.eval.year')}</label><input id="ev-year" type="number" min={2000} max={2100} value={year} onChange={(e) => setYear(e.target.value)} className={`${GS_COMPONENTS.input} sm:max-w-40`} /></div>
        {criteria.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {criteria.map((c) => (
              <div key={c.key} className="flex flex-col gap-1.5"><label htmlFor={`ev-${c.key}`} className={label}>{c.label}{evalConfig ? ` (/${evalConfig.maxScore})` : ''}</label><input id={`ev-${c.key}`} type="number" min={0} max={evalConfig?.maxScore} step="0.5" value={scores[c.key] ?? '0'} onChange={(e) => setScores((s) => ({ ...s, [c.key]: e.target.value }))} className={GS_COMPONENTS.input} /></div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5"><label htmlFor="ev-score" className={label}>{t('gest:management.eval.score')}{evalConfig ? ` (/${evalConfig.maxScore})` : ''}</label><input id="ev-score" type="number" min={0} step="0.5" value={singleScore} onChange={(e) => setSingleScore(e.target.value)} className={`${GS_COMPONENTS.input} sm:max-w-40`} /></div>
        )}
        <div className="flex flex-col gap-1.5"><label htmlFor="ev-comments" className={label}>{t('gest:management.eval.comments')}</label><textarea id="ev-comments" rows={4} value={comments} onChange={(e) => setComments(e.target.value)} className={GS_COMPONENTS.input} /></div>
        <ModalFooter onCancel={() => setOpen(false)} cancelLabel={t('gest:management.common.cancel')} submitLabel={t('gest:management.eval.submit')} onSubmit={() => send('submitted')} secondaryLabel={t('gest:management.eval.saveDraft')} onSecondary={() => send('draft')} pending={submit.isPending} />
      </Modal>
    </Card>
  );
}

// ─── Helpers partages ─────────────────────────────────────────────────────────

function PeriodFields({ startId, start, end, onStart, onEnd, t }: { startId: string; start: string; end: string; onStart: (v: string) => void; onEnd: (v: string) => void; t: (k: string) => string }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-1.5"><label htmlFor={`${startId}-start`} className={label}>{t('gest:management.period.start')}</label><input id={`${startId}-start`} type="date" value={start} onChange={(e) => onStart(e.target.value)} className={GS_COMPONENTS.input} /></div>
      <div className="flex flex-col gap-1.5"><label htmlFor={`${startId}-end`} className={label}>{t('gest:management.period.end')}</label><input id={`${startId}-end`} type="date" value={end} onChange={(e) => onEnd(e.target.value)} className={GS_COMPONENTS.input} /></div>
      <p className="col-span-2 text-chip text-slate-500 dark:text-slate-400">{t('gest:management.period.snapHint')}</p>
    </div>
  );
}

function ReasonField({ id, value, onChange, t }: { id: string; value: string; onChange: (v: string) => void; t: (k: string) => string }) {
  return (
    <div className="flex flex-col gap-1.5"><label htmlFor={id} className={label}>{t('gest:management.period.reason')}</label><input id={id} maxLength={300} value={value} onChange={(e) => onChange(e.target.value)} className={GS_COMPONENTS.input} /></div>
  );
}

// DTO camelCase -> forme PoC attendue par lib/salary/special-hours.
const toLibOverride = (o: HoursOverride) => ({ id: o.id, start_date: o.startDate, end_date: o.endDate, weekly_hours: o.weeklyHours, reason: o.reason, created_by_email: '', created_at: '' });
const toLibPremium = (p: RatePremium) => ({ id: p.id, start_date: p.startDate, end_date: p.endDate, premium_per_hour: p.premiumPerHour, reason: p.reason, created_by_email: '', created_at: '' });
